/**
 * Collections AR aging: the two definitions the dashboard server (functions/src/collections/refreshState.ts) and the
 * client (client/src/lib/collections-service.ts) must share, so they cannot disagree (#4905, #4906).
 */

/**
 * The business timezone: the finance team's "today" is the Pacific calendar date. THE single point to change if the
 * business ever bills across timezones (one organization today).
 */
export const COLLECTIONS_BUSINESS_TIMEZONE = "America/Los_Angeles";

/**
 * The reference date aging is measured from: the CALENDAR date in the business timezone, as a Date at 00:00 UTC of that
 * date. An invoice's DueDate is a date string parsed as 00:00 UTC, so `floor((reference - due) / day)` is then a whole
 * number of calendar days. Using the raw instant (as the code did, in a function running in UTC) makes every invoice
 * one day more overdue from 17:00 Pacific until midnight, when the UTC date has already rolled.
 * @param {Date} now the current instant
 * @param {string} timeZone IANA zone (default the business zone)
 * @return {Date} 00:00 UTC of the calendar date it is in `timeZone`
 */
export function agingReferenceDate(now: Date, timeZone: string = COLLECTIONS_BUSINESS_TIMEZONE): Date {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const part = (type: string): string => parts.find((p) => p.type === type)?.value ?? "";
  const ymd = `${part("year")}-${part("month")}-${part("day")}`;
  // Fail loudly: an unreadable date would turn every invoice's age into NaN and silently file all of them under "90+".
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) throw new Error(`agingReferenceDate: could not read a calendar date for ${timeZone} (got ${JSON.stringify(ymd)})`);
  const ref = new Date(`${ymd}T00:00:00.000Z`);
  if (Number.isNaN(ref.getTime())) throw new Error(`agingReferenceDate: could not read a calendar date for ${timeZone} (got ${JSON.stringify(ymd)})`);
  return ref;
}

/**
 * A customer is "current" when its OLDEST open invoice is not past due.
 * @param {number} oldestDaysLate days past due of the customer's oldest open invoice (<= 0 means not past due)
 * @return {boolean} whether the customer is current
 */
export function isCustomerCurrent(oldestDaysLate: number): boolean {
  // Fail closed: NaN (an unparseable DueDate) is NOT current.
  return Number.isFinite(oldestDaysLate) && oldestDaysLate <= 0;
}

/**
 * Percent of customers that are current. A CUSTOMER percentage: the numerator is a count of customers, never of invoices
 * (the field once divided an invoice count by a customer count and showed 316%).
 *
 * NOT capped: a numerator above the customer count is impossible for a customer count, so it means an invoice count was
 * fed in. That returns NaN ("unknown"), never a quiet 100% that would look healthier than the bug it replaces.
 * @param {number | undefined} currentCustomers customers whose oldest open invoice is not past due (undefined = not computed)
 * @param {number} customerCount all customers with an open balance
 * @return {number} 0..100; 0 when there are no customers; NaN when the inputs cannot be a customer percentage
 */
export function customersCurrentPercent(currentCustomers: number | undefined, customerCount: number): number {
  if (typeof currentCustomers !== "number" || !Number.isFinite(currentCustomers) || !Number.isFinite(customerCount)) return NaN;
  if (customerCount <= 0) return 0;
  if (currentCustomers < 0 || currentCustomers > customerCount) return NaN;
  return (currentCustomers / customerCount) * 100;
}
